import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Col, Table, Input, Label, ButtonGroup } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import moment from "moment";

// Utilidad REAL por artículo: venta neta − costo (el costo capturado al momento
// de la venta) − gastos. Incluye Pedidos, Venta Directa y Venta Directa Archivo,
// porque las tres generan renglones en ArticulosSurtidos.
function ReporteUtilidadArticulo() {
  const { user } = useContext(AuthContext);
  const URL_ARTICULOS_SURTIDOS = process.env.REACT_APP_URL_ARTICULOS_SURTIDOS;

  const hoy = new Date();
  const inicio30 = new Date();
  inicio30.setDate(inicio30.getDate() - 30);
  const endDate = moment(hoy).format("YYYY-MM-DD");
  const startDate = moment(inicio30).format("YYYY-MM-DD");

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [search, setSearch] = useState("");
  const [soloPerdida, setSoloPerdida] = useState(false);
  const [filas, setFilas] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const mxn = (n) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
      Number(n) || 0
    );
  const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);

  useEffect(() => {
    if (!selectedFechaInicio || !selectedFechaFin) return;
    showLoader();
    axios
      .get(
        `${URL_ARTICULOS_SURTIDOS}Utilidad/${selectedFechaInicio}/${selectedFechaFin}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((r) => {
        setFilas(r.data || []);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        setFilas([]);
        hideLoader();
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  const datos = useMemo(() => {
    let arr = [...filas];
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (r) =>
          (r.SKU || "").toLowerCase().includes(s) ||
          (r.nombre || "").toLowerCase().includes(s)
      );
    }
    if (soloPerdida) arr = arr.filter((r) => (Number(r.utilidadNeta) || 0) < 0);
    return arr;
  }, [filas, search, soloPerdida]);

  const totales = useMemo(
    () =>
      datos.reduce(
        (a, r) => ({
          cantidad: a.cantidad + (Number(r.cantidad) || 0),
          ventaNeta: a.ventaNeta + (Number(r.ventaNeta) || 0),
          costo: a.costo + (Number(r.costo) || 0),
          utilidadBruta: a.utilidadBruta + (Number(r.utilidadBruta) || 0),
          gastos: a.gastos + (Number(r.gastos) || 0),
          utilidadNeta: a.utilidadNeta + (Number(r.utilidadNeta) || 0),
        }),
        { cantidad: 0, ventaNeta: 0, costo: 0, utilidadBruta: 0, gastos: 0, utilidadNeta: 0 }
      ),
    [datos]
  );
  const margenTotal =
    totales.ventaNeta > 0 ? (totales.utilidadBruta / totales.ventaNeta) * 100 : 0;

  function dataPlana() {
    return datos.map((r) => ({
      SKU: r.SKU || "",
      Articulo: r.nombre || "",
      Cantidad: Number(r.cantidad) || 0,
      VentaNeta: Number(r.ventaNeta) || 0,
      Costo: Number(r.costo) || 0,
      UtilidadBruta: Number(r.utilidadBruta) || 0,
      Margen: Number(r.margen) || 0,
      Gastos: Number(r.gastos) || 0,
      UtilidadNeta: Number(r.utilidadNeta) || 0,
    }));
  }

  function PDFTabla() {
    const body = dataPlana().map((a) => [
      a.SKU,
      a.Articulo,
      num(a.Cantidad),
      mxn(a.VentaNeta),
      mxn(a.Costo),
      mxn(a.UtilidadBruta),
      `${a.Margen.toFixed(1)}%`,
      mxn(a.Gastos),
      mxn(a.UtilidadNeta),
    ]);
    const doc = new jsPDF("landscape", "mm", "a4", true);
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 8, 45, 15);
    const img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 125, 5, 30, 30);
    doc.text(`Utilidad por Artículo`, 20, 20);
    doc.text(`${selectedFechaInicio} - ${selectedFechaFin}`, 20, 28);
    doc.autoTable({
      head: [
        [
          "SKU",
          "Artículo",
          "Cant.",
          "Venta Neta",
          "Costo",
          "Utilidad Bruta",
          "Margen",
          "Gastos",
          "Utilidad Neta",
        ],
      ],
      body,
      startY: 34,
      foot: [
        [
          "",
          "TOTAL",
          num(totales.cantidad),
          mxn(totales.ventaNeta),
          mxn(totales.costo),
          mxn(totales.utilidadBruta),
          `${margenTotal.toFixed(1)}%`,
          mxn(totales.gastos),
          mxn(totales.utilidadNeta),
        ],
      ],
      footStyles: { fontSize: 10, fillColor: [255, 255, 255], textColor: [0, 0, 0] },
    });
    doc.save(`UtilidadPorArticulo.pdf`);
  }

  function excel() {
    const option = {};
    option.fileName = "UtilidadPorArticulo";
    option.datas = [
      {
        sheetData: dataPlana(),
        sheetName: "Utilidad",
        sheetFilter: [
          "SKU",
          "Articulo",
          "Cantidad",
          "VentaNeta",
          "Costo",
          "UtilidadBruta",
          "Margen",
          "Gastos",
          "UtilidadNeta",
        ],
        sheetHeader: [
          "SKU",
          "Artículo",
          "Cantidad",
          "Venta Neta",
          "Costo",
          "Utilidad Bruta",
          "Margen %",
          "Gastos",
          "Utilidad Neta",
        ],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  }

  const colorUtil = (v) => ({
    color: (Number(v) || 0) < 0 ? "#dc2626" : "#15803d",
    fontWeight: 700,
  });

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_pedidos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuVentas"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Utilidad por Artículo</h3>
          <div align="center" style={{ fontSize: 12, color: "#64748b" }}>
            Venta neta − costo real de venta − gastos. Incluye Pedidos, Venta
            Directa y Venta Directa Archivo.
          </div>
          <br />
          <Row className="align-items-end">
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Buscar</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="SKU o artículo"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <div className="d-flex align-items-center" style={{ height: 31 }}>
                <Input
                  type="checkbox"
                  checked={soloPerdida}
                  onChange={(e) => setSoloPerdida(e.target.checked)}
                  style={{ position: "static", margin: "0 6px 0 0" }}
                />
                <span style={{ fontSize: 13 }}>Sólo los que pierden dinero</span>
              </div>
            </Col>
          </Row>
          <br />
          {datos.length === 0 ? (
            <h5 align="center" style={{ color: "#64748b" }}>
              Sin ventas en el periodo seleccionado
            </h5>
          ) : (
            <Table size="sm" striped borderless className="table-responsive-xl">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Artículo</th>
                  <th className="text-right">Cant.</th>
                  <th className="text-right">Venta Neta</th>
                  <th className="text-right">Costo</th>
                  <th className="text-right">Utilidad Bruta</th>
                  <th className="text-right">Margen</th>
                  <th className="text-right">Gastos</th>
                  <th className="text-right">Utilidad Neta</th>
                </tr>
              </thead>
              <tbody>
                {datos.map((r) => (
                  <tr key={r.idArticulo}>
                    <td>{r.SKU}</td>
                    <td>{r.nombre}</td>
                    <td className="text-right">{num(r.cantidad)}</td>
                    <td className="text-right">{mxn(r.ventaNeta)}</td>
                    <td className="text-right">{mxn(r.costo)}</td>
                    <td className="text-right" style={colorUtil(r.utilidadBruta)}>
                      {mxn(r.utilidadBruta)}
                    </td>
                    <td className="text-right" style={colorUtil(r.margen)}>
                      {(Number(r.margen) || 0).toFixed(1)}%
                    </td>
                    <td className="text-right">{mxn(r.gastos)}</td>
                    <td className="text-right" style={colorUtil(r.utilidadNeta)}>
                      {mxn(r.utilidadNeta)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: "2px solid #cbd5e1" }}>
                  <td></td>
                  <td className="text-right">TOTAL</td>
                  <td className="text-right">{num(totales.cantidad)}</td>
                  <td className="text-right">{mxn(totales.ventaNeta)}</td>
                  <td className="text-right">{mxn(totales.costo)}</td>
                  <td className="text-right" style={colorUtil(totales.utilidadBruta)}>
                    {mxn(totales.utilidadBruta)}
                  </td>
                  <td className="text-right" style={colorUtil(margenTotal)}>
                    {margenTotal.toFixed(1)}%
                  </td>
                  <td className="text-right">{mxn(totales.gastos)}</td>
                  <td className="text-right" style={colorUtil(totales.utilidadNeta)}>
                    {mxn(totales.utilidadNeta)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default ReporteUtilidadArticulo;
